import React from "react";
import imageMap from "./ImageMap";
// Function to replace keywords with images.
const formatTextWithImages = (text) => {
  if (!text) return "";
  return text
    .split(
      /(redPuzzle|bluePuzzle|greenPuzzle|redBox|blueBox|greenBox|rightArrow|leftArrow|storyEvent|Activation|Movement|aiCard|enDeavour)/g
    )
    .map((part, index) => {
      if (part === "Activation" || part === "Movement") {
        const filename = part + ".jpeg"; // Preserve the asset filename case on Linux.
        return (
          <img
            key={index}
            src={`/assets/${filename}`}
            alt={part}
            style={{ width: "16px", height: "16px", margin: "0 3px" }}
          />
        );
      } else if (imageMap[part]) {
        return (
          <img
            key={index}
            src={`${process.env.PUBLIC_URL}/assets/${imageMap[part]}`}
            alt={part}
            style={{ width: "20px", height: "20px", margin: "0 3px" }}
            onError={(e) => {
              // fail-safe so you can see it instead of silently missing
              e.currentTarget.src = `${process.env.PUBLIC_URL}/assets/missing.jpeg`;
            }}
          />
        );
      } else if (part === "storyEvent") {
        return "📖";
      } else {
        return part;
      }
    });
};

// Function to render an affinity box (for gear cards)
const getAffinityBox = (affinity, position) => {
  if (!affinity || affinity === "blank") return null;
  let color = "gray";
  const lower = affinity.toLowerCase();
  if (lower.includes("red")) {
    color = "red";
  } else if (lower.includes("blue")) {
    color = "blue";
  } else if (lower.includes("green")) {
    color = "green";
  }
  return (
    <div className={`affinity ${position}`} style={{ backgroundColor: color }}></div>
  );
};

// Parse **bold** while still applying icon replacement inside the text.
const renderWithBold = (text) => {
  if (!text) return "";

  // Split into segments, keeping **...** chunks
  const parts = text.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, i) => {
    const isBold =
      part.startsWith("**") && part.endsWith("**") && part.length >= 4;
    if (isBold) {
      const inner = part.slice(2, -2);
      return <strong key={i}>{formatTextWithImages(inner)}</strong>;
    }
    return <React.Fragment key={i}>{formatTextWithImages(part)}</React.Fragment>;
  });
};

// Format descriptions with line breaks + bold parsing + icons
const formatDescription = (text) => {
  if (!text) return "";
  return text.split("\n").map((line, index) => (
    <React.Fragment key={index}>
      {renderWithBold(line)}
      <br />
    </React.Fragment>
  ));
};

// Split affinityDescription into two boxes.
const formatAffinityDescription = (text) => {
  if (!text) return "";

  const affinityTokens = [
    "redPuzzle",
    "greenPuzzle",
    "bluePuzzle",
    "redBox",
    "greenBox",
    "blueBox",
  ];
  const inlineTokens = ["aiCard", "Activation", "Movement"];

  let leftMatches = [];
  let remainingText = text;
  let foundToken = true;

  while (foundToken) {
    foundToken = false;
    for (const token of affinityTokens) {
      if (remainingText.startsWith(token)) {
        leftMatches.push(token);
        remainingText = remainingText.slice(token.length).trimStart();
        foundToken = true;
        break;
      }
    }
  }

  const formattedText = remainingText
    .split(/(aiCard|Activation|Movement)/g)
    .map((part, index) => {
      if (inlineTokens.includes(part)) {
        return (
          <span key={index} className="inline-art-container">
            <span className="inline-space"> </span>
            <img
              src={`/assets/${imageMap[part] || part + ".jpeg"}`}
              alt={part}
              className="inline-art-image"
            />
            <span className="inline-space"> </span>
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });

  if (leftMatches.length > 0) {
    return (
      <div className="affinity-description-split-container">
        <div className="affinity-art-left">
          {leftMatches.map((token, index) => (
            <img
              key={index}
              src={`/assets/${imageMap[token] || token + ".jpeg"}`}
              alt={token}
              className="affinity-art-image"
            />
          ))}
        </div>
        <div className="affinity-text-right">{formattedText}</div>
      </div>
    );
  }

  return <div className="affinity-text-right">{formattedText}</div>;
};

const getArmorImageSrc = (bodyPart) => {
  if (!bodyPart) return "";
  if (Array.isArray(bodyPart)) {
    const validParts = bodyPart.filter(
      (bp) => typeof bp === "string" && bp.trim() !== ""
    );
    if (validParts.length === 0) return "";
    return validParts.map(
      (bp) => `/assets/${bp.toLowerCase().replace(/\s+/g, "")}.jpeg`
    );
  }
  if (typeof bodyPart === "string" && bodyPart.trim() !== "") {
    return [`/assets/${bodyPart.toLowerCase().replace(/\s+/g, "")}.jpeg`];
  }
  return "";
};

// Render content for non-gear cards
const renderContent = (item, type) => {
  switch (type) {
    case "glossary":
    case "abilities":
    case "impairments":
      return (
        <div className="glossary-content">
          <h3 className="glossary-term">{item.term}</h3>
          <p className="glossary-definition">{formatDescription(item.definition)}</p>
        </div>
      );

    case "disorders":
      return (
        <div className="glossary-content">
          <h3 className="glossary-term">{item.term}</h3>

          {/* Flavor text: italic + SAME SIZE as other text */}
          {item["flavor text"] && String(item["flavor text"]).trim() !== "" && (
            <p className="notes">
              <em>{formatDescription(item["flavor text"])}</em>
            </p>
          )}

          {/* Definition: SAME SIZE as flavor/notes */}
          {item.definition && String(item.definition).trim() !== "" && (
            <p className="notes">{formatDescription(item.definition)}</p>
          )}

          {/* Expansion: show BELOW everything */}
          {item.expansion && String(item.expansion).trim() !== "" && (
            <p className="notes">
              <strong>Expansion:</strong> {item.expansion}
            </p>
          )}
        </div>
      );

    case "fightingArts":
    case "secretFightingArts":
      return (
        <div className="glossary-content">
          <h3 className="glossary-term">{item.name}</h3>
          <p className="glossary-definition">{formatDescription(item.definition)}</p>
          {item.notes && <p className="notes">{formatDescription(item.notes)}</p>}
        </div>
      );

    case "strainFightingArts":
      return (
        <div className="glossary-content">
          <h3 className="glossary-term">
            {item.name} {item.level && `(${item.level})`}
          </h3>
          <p className="glossary-definition">{formatDescription(item.rules)}</p>
          {item.observationCondition && (
            <p className="notes">
              <strong>Observation:</strong> {formatDescription(item.observationCondition)}
            </p>
          )}
          {item.boxes && (
            <p className="notes">
              <strong>Boxes:</strong> {item.boxes}
            </p>
          )}
          {item.levelUp && (
            <p className="notes">
              <strong>Level Up:</strong> {item.levelUp}
            </p>
          )}
        </div>
      );

    case "weaponProficiencies":
      return (
        <div className="glossary-content">
          <h3 className="glossary-term">{item.name}</h3>
          <p className="glossary-definition">
            <strong>Specialization:</strong> {formatTextWithImages(item.specialization)}
          </p>
          <p className="glossary-definition">
            <strong>Mastery:</strong> {formatTextWithImages(item.mastery)}
          </p>
          {item.notes && (
            <p className="notes">
              <strong>Notes:</strong> {formatDescription(item.notes)}
            </p>
          )}
        </div>
      );

    case "knowledge":
      return (
        <div className="glossary-content">
          <h3 className="glossary-term">
            {item.name} {item.level && `(${item.level})`}
          </h3>
          {item.lumiCost !== undefined && (
            <p className="lumiCost">
              <strong>Lumi Cost:</strong> {item.lumiCost}
            </p>
          )}
          {item.traits && (
            <p className="traits">
              <strong>Traits:</strong> {item.traits}
            </p>
          )}
          <p className="glossary-definition">
            <strong>Rules:</strong> {formatDescription(item.rules)}
          </p>
          {item.observationCondition && (
            <p className="notes">
              <strong>Observation:</strong> {formatDescription(item.observationCondition)}
            </p>
          )}
          {item.boxes && (
            <p className="notes">
              <strong>Boxes:</strong> {item.boxes}
            </p>
          )}
          {item.levelUp && (
            <p className="notes">
              <strong>Level Up:</strong> {item.levelUp}
            </p>
          )}
        </div>
      );

    case "philosophy":
      return (
        <div className="glossary-content">
          <h3 className="glossary-term">{item.name}</h3>
          {item.neurosis && (
            <p className="traits">
              <strong>Neurosis:</strong> {item.neurosis}
            </p>
          )}
          {item.neurosisDefinition && (
            <p className="glossary-definition">
              <strong>Neurosis Definition:</strong> {formatDescription(item.neurosisDefinition)}
            </p>
          )}
          {item.tenet && (
            <p className="notes">
              <strong>Tenet:</strong> {item.tenet}
            </p>
          )}
          {item.huntxp && (
            <p className="notes">
              <strong>Hunt XP:</strong> {item.huntxp.join(", ")}
            </p>
          )}
          {item.type && (
            <p className="notes">
              <strong>Type:</strong> {item.type}
            </p>
          )}
          {item.flavor && (
            <p className="flavor-text">
              <strong>Flavor:</strong> {item.flavor}
            </p>
          )}
        </div>
      );

    case "innovation":
      return (
        <div className="glossary-content">
          <h3 className="glossary-term">{item.name}</h3>

          {item["innovation type"] && (
            <p className="notes">
              <strong>Type:</strong> {item["innovation type"]}
            </p>
          )}

          {item["consequence of"] && String(item["consequence of"]).trim() !== "" && (
            <p className="notes">
              <strong>Consequence of:</strong> {item["consequence of"]}
            </p>
          )}

          {item.expansion && String(item.expansion).trim() !== "" && (
            <p className="notes">
              <strong>Expansion:</strong> {item.expansion}
            </p>
          )}

          {item.text ? (
            <p className="glossary-definition">
              <strong>Text:</strong> {formatDescription(item.text)}
            </p>
          ) : null}
        </div>
      );

    // ✅ FIXED: Resources formatting
    case "resources":
      return (
        <div className="glossary-content">
          <h3 className="glossary-term">{item.name}</h3>

          {item.expansion && String(item.expansion).trim() !== "" && (
            <p className="notes">
              <strong>Expansion:</strong> {item.expansion}
            </p>
          )}

          {item.location && String(item.location).trim() !== "" && (
            <p className="notes">
              <strong>Location:</strong> {item.location}
            </p>
          )}

          {/* Italic flavorText */}
          {item.flavorText && String(item.flavorText).trim() !== "" && (
            <p className="flavor-text">
              <em>{formatDescription(item.flavorText)}</em>
            </p>
          )}

          {/* No "Text:" label + normal styling + no placeholder */}
          {item.text && String(item.text).trim() !== "" && (
            <p className="notes">{formatDescription(item.text)}</p>
          )}

          {(item.material && String(item.material).trim() !== "") ||
          item.consumable !== undefined ? (
            <div className="notes">
              {item.material && String(item.material).trim() !== "" ? (
                <p className="notes">
                  <strong>Material:</strong> {item.material}
                </p>
              ) : null}

              {item.consumable !== undefined ? (
                <p className="notes">
                  <strong>Consumable:</strong> {item.consumable ? "Yes" : "No"}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      );

    case "settlementEvents":
      return (
        <div className="glossary-content settlement-event-card">
          <h3 className="glossary-term">{item.name}</h3>

          {item.Tags && (
            <p className="notes">
              <span className="se-label">Tags:</span> {String(item.Tags)}
            </p>
          )}

          {item.rules && (
            <p className="glossary-definition">
              <span className="se-label">Rules:</span> {formatDescription(item.rules)}
            </p>
          )}

          {(item["Lingering Effects"] || item.lingeringEffects) && (
            <p className="notes">
              <span className="se-label">Lingering Effects:</span>{" "}
              {formatDescription(item["Lingering Effects"] || item.lingeringEffects)}
            </p>
          )}
        </div>
      );

    case "scoutDiscovery":
      return (
        <div className="glossary-content">
          <h3 className="glossary-term">{item.name}</h3>

          {item.Monster ? (
            <p className="notes">
              <strong>Monster:</strong> {item.Monster}
            </p>
          ) : null}

          {item.rules ? (
            <p className="glossary-definition">
              <strong>Rules:</strong> {formatDescription(item.rules)}
            </p>
          ) : null}
        </div>
      );

    default:
      return (
        <div className="glossary-content">
          <h3 className="glossary-term">{item.term || item.name}</h3>
          <p className="glossary-definition">
            {formatDescription(item.definition || item.rules)}
          </p>
        </div>
      );
  }
};

// Render gear cards using renderItem
const renderItem = (item) => {
  if (item.cardType === "gear") {
    const multipleParts = Array.isArray(item.bodyPart) && item.bodyPart.length > 1;
    return (
      <>
        {item.affinity && (
          <div className="affinity-container">
            {getAffinityBox(item.affinity?.split(", ")[0], "top")}
            {getAffinityBox(item.affinity?.split(", ")[1], "right")}
            {getAffinityBox(item.affinity?.split(", ")[2], "bottom")}
            {getAffinityBox(item.affinity?.split(", ")[3], "left")}
          </div>
        )}
        <div className="gear-header" style={{ display: "flex", alignItems: "center" }}>
          {item.bodyPart && (
            <div className={`armor-container ${multipleParts ? "multiple" : ""}`}>
              {item.armourPoints != null && <div className="shield">{item.armourPoints}</div>}
              {Array.isArray(item.bodyPart)
                ? item.bodyPart.map((bp, idx) => {
                    const imgPaths = getArmorImageSrc(bp);
                    const imgSrc = imgPaths[0];
                    return (
                      <img
                        key={idx}
                        src={imgSrc}
                        alt={bp}
                        className="body-part-img"
                        onError={(e) => {
                          if (e.target.src.endsWith(".jpeg")) {
                            e.target.src = e.target.src.replace(".jpeg", ".jpg");
                          } else {
                            e.target.src = process.env.PUBLIC_URL + "/assets/missing.jpeg";
                          }
                        }}
                      />
                    );
                  })
                : (
                  <img
                    src={getArmorImageSrc(item.bodyPart)[0]}
                    alt={item.bodyPart}
                    className="body-part-img"
                    onError={(e) => {
                      if (e.target.src.endsWith(".jpeg")) {
                        e.target.src = e.target.src.replace(".jpeg", ".jpg");
                      } else {
                        e.target.src = process.env.PUBLIC_URL + "/assets/missing.jpeg";
                      }
                    }}
                  />
                )}
            </div>
          )}
          <div className="entry-title">{item.name}</div>
        </div>
        <p className={`gear-keywords ${item.numbers && item.numbers.length > 0 ? "with-numbers" : ""}`}>
          {item.keywords}
        </p>
        {item.affinity && (
          <div className="affinity center">
            {(() => {
              const parts = item.affinity.split(", ");
              if (parts[4] === "RGB") {
                return (
                  <div className="rgb-container">
                    {getAffinityBox("redBox", "center")}
                    {getAffinityBox("greenBox", "center")}
                    {getAffinityBox("blueBox", "center")}
                  </div>
                );
              }
              return getAffinityBox(parts[4], "center");
            })()}
          </div>
        )}
        {Array.isArray(item.numbers) && item.numbers.length > 0 && (
          <div className="numbers-container">
            {item.numbers.length <= 2
              ? item.numbers.map((num, idx) => (
                  <div key={idx} className="number darker">{num}</div>
                ))
              : (
                <>
                  {item.numbers.slice(0, item.numbers.length - 2).map((num, idx) => (
                    <div key={idx} className="number lighter">{num}</div>
                  ))}
                  {item.numbers.slice(-2).map((num, idx) => (
                    <div key={idx} className="number darker">{num}</div>
                  ))}
                </>
              )}
          </div>
        )}
        <p className="gear-description">{formatDescription(item.description)}</p>
        {item.affinityDescription && (
          <div className="affinity-description-box">
            {formatAffinityDescription(item.affinityDescription)}
          </div>
        )}
      </>
    );
  }

  return renderContent(item, item.cardType);
};


export { renderItem, formatTextWithImages };
